// ----------------------------------------------------------------------
// <copyright file="Izenda.SettingsByRange.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { action, computed, observable } from 'mobx';
import { DATA_TYPE, THRESHOLD, VALUE_TYPES } from 'logic/data/Izenda.SettingsByRangeConstants';
import {
  clone,
  defaults,
  filter,
  find,
  forEach,
  get,
  getOr,
  isEmpty,
  isNil,
  last,
  map,
  uniq,
  uniqueId,
  chain
} from 'lodash/fp';
import SettingsByRangeItemFieldModel from 'logic/models/Izenda.SettingsByRangeItemFieldModel';

export default class SettingsByRangeModel {

  @observable
  selectedValueType = 'value';

  @observable
  settingsValue = {
    none: [],
    value: [],
    rangeValue: [],
    rangePercent: [],
    allValues: []
  };

  @observable
  editMode = false;

  @computed
  get selectedSettingsList() {
    return this.settingsValue[this.selectedValueType];
  }

  setValueByType(valueType, newSettingsValue) {
    const newValues = newSettingsValue[valueType];
    if (newValues) {
      const valueTypeDef = VALUE_TYPES[valueType] || {};
      const defaultValue = valueTypeDef.dataStructure || {};
      const newValuesPrepared = map(x => {
        const withDefaults = defaults(defaultValue, x);
        withDefaults.fields = map(f => {
          const model = new SettingsByRangeItemFieldModel();
          model.populateFieldFromObject(f);
          return model;
        }, withDefaults.fields);
        return withDefaults;
      }, newValues);
      this.settingsValue[valueType] = observable(newValuesPrepared);
      this.selectedValueType = valueType;
      this.editMode = true;
    } else {
      this.settingsValue[valueType] = [];
    }
  }

  setSettingValue = function (newSettingsValue) {

    newSettingsValue = newSettingsValue || {};

    this.setValueByType('none', newSettingsValue);
    this.setValueByType('value', newSettingsValue);
    this.setValueByType('rangeValue', newSettingsValue);
    this.setValueByType('rangePercent', newSettingsValue);
    this.setValueByType('allValues', newSettingsValue);
  }

  disableEditMode() {
    this.editMode = false;
  }

  /**
   * Only get the current selected setting value
   * @returns {*}
   */
  getSettingValue() {
    this.removeUnSelectSettingValue();
    const result = {};
    result[this.selectedValueType] = observable(this.selectedSettingsList);
    if (isEmpty(result['rangeValue']))
      result.rangeValue = null;
    if (isEmpty(result['rangePercent']))
      result.rangePercent = null;
    if (isEmpty(result['allValues']))
      result.allValues = null;
    if (isEmpty(result[this.selectedValueType]))
      result[this.selectedValueType] = null;
    return result;
  }

  @action
  removeUnSelectSettingValue() {
    const backupValue = this.selectedSettingsList;
    this.settingsValue.value = [];
    this.settingsValue.rangeValue = [];
    this.settingsValue.rangePercent = [];
    this.settingsValue.allValues = [];
    this.settingsValue[this.selectedValueType] = backupValue;
    this.removeEmptySettingValue();
  }

  /**
   * Remove empty setting value rows
   * @param {boolean} shouldCheckName indicator for if the value has a name property that should exist
   * @param {boolean} allowNullValueOverwrite  indicator for if null or blank values are able to overwritten
   */
  @action
  removeEmptySettingValue(shouldCheckName, allowNullValueOverwrite) {
    const selectedValue = this.selectedValueType;
    this.settingsValue[selectedValue] = this.settingsValue[selectedValue].filter(item => {
      if (selectedValue === 'value') {
        if (item.thresholdType !== THRESHOLD.THRESHOLD_TYPES.DYNAMIC.value)
          return allowNullValueOverwrite ? !!item.text : !!item.key || !!item.text;
        else
          return true;
      } else {
        return shouldCheckName
          ? !!item.from || !!item.to || !!item.text || !!item.name
          : !!item.from || !!item.to || !!item.text;
      }
    });
  }

  /**
   * Check a Row whether it have un-complete value which lack of from - to - name value
   * @param {boolean} shouldCheckName
   * @returns {boolean}
   */
  isExistsUncompleteRangeValue(shouldCheckName) {
    if (this.selectedValueType === 'none' || this.selectedValueType === 'value')
      return false;

    const haveData = value => !!(value || value === 0);
    const rangeValue = find(item => {
      let isThisRowUnComplete = false;
      if (shouldCheckName) {
        // logical xor
        if (!(haveData(item.from) === haveData(item.to) && haveData(item.to) === haveData(item.name))) {
          isThisRowUnComplete = true;
        }
      } else {
        // logical xor
        if (!(haveData(item.from) === haveData(item.to))) {
          isThisRowUnComplete = true;
        }
      }
      return isThisRowUnComplete;
    }, this.selectedSettingsList);
    return !!rangeValue;
  }

  /**
   * Check constrains
   * It have checking duplication of value or range value
   * @returns {Array}
   */
  checkConstrains(dataType, cycle) {
    const selectedValue = this.selectedValueType;

    const isItemHasDuplicateKeys = (item1, item2) => {
      if (item1.thresholdType !== item2.thresholdType)
        return false;
      if (item1.thresholdType === 'dynamic') {
        return getOr(null, 'fields[0].fieldId', item1) === getOr(null, 'fields[0].fieldId', item2) &&
          getOr(null, 'functionId', item1) === getOr(null, 'functionId', item2);
      } else if (item1.thresholdType === 'dynamic_subtotal') {
        return getOr(null, 'functionId', item1) === getOr(null, 'functionId', item2);
      } else {
        return item1.key === item2.key;
      }
    };

    const list = this.settingsValue[selectedValue];
    let i = 0;
    let j = 0;
    let duplicate = [];
    let invalidRange = [];
    let invalidIndex = {};
    let duplicateIndex = {};

    function checkDuplicateValue() {
      for (; i <= list.length - 1; i++) {
        if (!isNil(duplicateIndex[i])) {
          continue;
        }

        for (j = i + 1; j <= list.length - 1; j++) {
          if (!isNil(duplicateIndex[j])) {
            continue;
          }

          if (isItemHasDuplicateKeys(list[i], list[j])) {
            duplicate.push(list[j]);
            duplicateIndex[j] = j;
            if (isNil(duplicateIndex[i])) {
              duplicate.push(list[i]);
              duplicateIndex[i] = i;
            }
          }
        }
      }
    }

    function registerInvalidRange(index) {
      if (isNil(invalidIndex[index])) {
        invalidRange.push(list[index]);
        invalidIndex[index] = list[index];
      }
    }

    function checkRangeOverlapCycle(firstValue, secondValue) {
      return (
        firstValue.from > firstValue.to &&
        (secondValue.from > firstValue.from ||
          secondValue.to > firstValue.from ||
          secondValue.from < firstValue.to ||
          secondValue.to < firstValue.to)
      );
    }

    function checkDuplicateRangeValue() {
      if (!cycle) {
        for (; i <= list.length - 1; i++) {
          if (list[i].from >= list[i].to) {
            registerInvalidRange(i);
            continue;
          }
          for (j = i + 1; j <= list.length - 1; j++) {
            if (list[j].from >= list[j].to) {
              registerInvalidRange(j);
              continue;
            }
            list[j].from < list[i].to && list[i].from < list[j].to && duplicate.push(list[i], list[j]);
          }
        }
      } else {
        for (; i <= list.length - 1; i++) {
          for (j = i + 1; j <= list.length - 1; j++) {
            if (
              checkRangeOverlapCycle(list[i], list[j]) ||
              checkRangeOverlapCycle(list[j], list[i]) ||
              (list[i].from < list[i].to &&
                list[j].from < list[j].to &&
                list[j].from < list[i].to &&
                list[i].from < list[j].to)
            ) {
              duplicate.push(list[i], list[j]);
            }
          }
        }
      }
    }

    if (selectedValue === 'value' || selectedValue === 'allValues') {
      checkDuplicateValue();
    } else {
      checkDuplicateRangeValue();
    }
    duplicate = uniq(duplicate);
    return {
      duplicate: duplicate,
      invalidRange: invalidRange
    };
  }

  checkDuplicateName() {
    const list = this.selectedSettingsList;
    const duplicate = [];

    for (let i = 0; i <= list.length - 2; i++) {
      for (let j = i + 1; j <= list.length - 1; j++) {
        list[i].name === list[j].name && duplicate.push(list[i], list[j]);
      }
    }
    return duplicate;
  }

  checkEmptyDynamicValues() {
    const result = [];
    const list = this.selectedSettingsList;
    forEach(item => {
      if (item.thresholdType === THRESHOLD.THRESHOLD_TYPES.DYNAMIC.value) {
        const fieldName = getOr(null, 'fields[0].fieldName', item);
        if (!fieldName)
          result.push(item);
      }
    }, list);
    return result;
  }

  checkEmptyDynamicSubtotalValues() {
    return chain(this.selectedSettingsList)
      .filter({ thresholdType: THRESHOLD.THRESHOLD_TYPES.DYNAMIC_SUBTOTAL.value })
      .filter(x => !x.functionId || isEmpty(x.functionId))
      .value();
  }

  setValueType(valueType) {
    this.selectedValueType = valueType;
  }

  setValueTypeByDataType(dataType, dateTimeFormatData) {
    // Only set default value for add new
    if (!this.editMode) {
      switch (dataType) {
        case DATA_TYPE.TEXT:
          this.setValueType('value');
          break;
        case DATA_TYPE.MONEY:
        case DATA_TYPE.NUMERIC:
        case DATA_TYPE.TIME:
          this.setValueType('rangeValue');
          break;
        case DATA_TYPE.DATETIME:
          const settingsValueType = getOr(
            ['value', 'rangeValue'],
            'settingByGroupBy.SETTINGS_VALUE_TYPE',
            dateTimeFormatData
          );

          this.setValueType(last(settingsValueType));
          break;
        case 'none':
          this.setValueType('none');
          break;
        default:
          this.setValueType('value');
      }
    }
  }

  addNewSetting(data) {
    const valueType = this.selectedValueType;

    if (data) {
      this.selectedSettingsList.unshift(clone(data));
      return;
    }
    const valueTypeDef = VALUE_TYPES[valueType] || {};
    let dataStructure = valueTypeDef.dataStructure || {};
    dataStructure = clone(dataStructure);
    dataStructure.id = uniqueId('setting_');
    this.selectedSettingsList.unshift(dataStructure);
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.SettingsByRangeModel.js